import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../services/api';

// Async thunks
export const fetchLeads = createAsyncThunk(
  'leads/fetchLeads',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.getLeads(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchMyLeads = createAsyncThunk(
  'leads/fetchMyLeads',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.getMyLeads(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchVendorLeads = createAsyncThunk(
  'leads/fetchVendorLeads',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.getVendorLeads(params);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchLead = createAsyncThunk(
  'leads/fetchLead',
  async (leadId, { rejectWithValue }) => {
    try {
      const response = await api.getLead(leadId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const createLead = createAsyncThunk(
  'leads/createLead',
  async (leadData, { rejectWithValue }) => {
    try {
      const response = await api.createLead(leadData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateLead = createAsyncThunk(
  'leads/updateLead',
  async ({ leadId, leadData }, { rejectWithValue }) => {
    try {
      const response = await api.updateLead(leadId, leadData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const deleteLead = createAsyncThunk(
  'leads/deleteLead',
  async (leadId, { rejectWithValue }) => {
    try {
      await api.deleteLead(leadId);
      return leadId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchLeadMessages = createAsyncThunk(
  'leads/fetchLeadMessages',
  async (leadId, { rejectWithValue }) => {
    try {
      const response = await api.getLeadMessages(leadId);
      return { leadId, messages: response.data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const sendLeadMessage = createAsyncThunk(
  'leads/sendLeadMessage',
  async ({ leadId, messageData }, { rejectWithValue }) => {
    try {
      const response = await api.sendLeadMessage(leadId, messageData);
      return { leadId, message: response.data };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const markLeadMessagesAsRead = createAsyncThunk(
  'leads/markLeadMessagesAsRead',
  async (leadId, { rejectWithValue }) => {
    try {
      await api.markLeadMessagesAsRead(leadId);
      return leadId;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

const initialState = {
  leads: [],
  currentLead: null,
  leadMessages: {},
  loading: false,
  error: null,
  pagination: {
    current: 1,
    pageSize: 10,
    total: 0,
  },
  filters: {
    status: null,
    vendor_id: null,
    customer_id: null,
    event_type: null,
    date_from: null,
    date_to: null,
  },
};

const leadSlice = createSlice({
  name: 'leads',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setCurrentLead: (state, action) => {
      state.currentLead = action.payload;
    },
    clearCurrentLead: (state) => {
      state.currentLead = null;
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        status: null,
        vendor_id: null,
        customer_id: null,
        event_type: null,
        date_from: null,
        date_to: null,
      };
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
    addMessageToLead: (state, action) => {
      const { leadId, message } = action.payload;
      if (!state.leadMessages[leadId]) {
        state.leadMessages[leadId] = [];
      }
      state.leadMessages[leadId].push(message);
    },
    updateLeadStatus: (state, action) => {
      const { leadId, status, updatedAt } = action.payload;
      const lead = state.leads.find(l => l.id === leadId);
      if (lead) {
        lead.status = status;
        lead.updated_at = updatedAt;
      }
      if (state.currentLead && state.currentLead.id === leadId) {
        state.currentLead.status = status;
        state.currentLead.updated_at = updatedAt;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Leads
      .addCase(fetchLeads.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLeads.fulfilled, (state, action) => {
        state.loading = false;
        const data = action.payload;
        if (data.data) {
          state.leads = data.data;
          state.pagination = {
            current: data.current_page || 1,
            pageSize: data.per_page || 10,
            total: data.total || 0,
          };
        } else {
          state.leads = Array.isArray(data) ? data : [];
        }
      })
      .addCase(fetchLeads.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch My Leads
      .addCase(fetchMyLeads.fulfilled, (state, action) => {
        const data = action.payload;
        state.leads = Array.isArray(data) ? data : (data.data || []);
      })
      // Fetch Vendor Leads
      .addCase(fetchVendorLeads.fulfilled, (state, action) => {
        const data = action.payload;
        state.leads = Array.isArray(data) ? data : (data.data || []);
      })
      // Fetch Single Lead
      .addCase(fetchLead.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLead.fulfilled, (state, action) => {
        state.loading = false;
        state.currentLead = action.payload;
      })
      .addCase(fetchLead.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create Lead
      .addCase(createLead.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createLead.fulfilled, (state, action) => {
        state.loading = false;
        state.leads.unshift(action.payload);
      })
      .addCase(createLead.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update Lead
      .addCase(updateLead.fulfilled, (state, action) => {
        const updatedLead = action.payload;
        const index = state.leads.findIndex(l => l.id === updatedLead.id);
        if (index !== -1) {
          state.leads[index] = updatedLead;
        }
        if (state.currentLead && state.currentLead.id === updatedLead.id) {
          state.currentLead = updatedLead;
        }
      })
      // Delete Lead
      .addCase(deleteLead.fulfilled, (state, action) => {
        const leadId = action.payload;
        state.leads = state.leads.filter(l => l.id !== leadId);
        if (state.currentLead && state.currentLead.id === leadId) {
          state.currentLead = null;
        }
      })
      // Fetch Lead Messages
      .addCase(fetchLeadMessages.fulfilled, (state, action) => {
        const { leadId, messages } = action.payload;
        state.leadMessages[leadId] = messages;
      })
      // Send Lead Message
      .addCase(sendLeadMessage.fulfilled, (state, action) => {
        const { leadId, message } = action.payload;
        if (!state.leadMessages[leadId]) {
          state.leadMessages[leadId] = [];
        }
        state.leadMessages[leadId].push(message);
      })
      // Mark Lead Messages as Read
      .addCase(markLeadMessagesAsRead.fulfilled, (state, action) => {
        const leadId = action.payload;
        if (state.leadMessages[leadId]) {
          state.leadMessages[leadId].forEach(message => {
            message.read_at = new Date().toISOString();
          });
        }
      });
  },
});

export const {
  clearError,
  setCurrentLead,
  clearCurrentLead,
  setFilters,
  clearFilters,
  setPagination,
  addMessageToLead,
  updateLeadStatus,
} = leadSlice.actions;

export default leadSlice.reducer;
